import * as React from 'react';
import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import SettingsIcon from '@material-ui/icons/Settings';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import { useMediaQuery, Theme, IconButton, Tooltip } from '@material-ui/core';
import { useTranslate, MenuItemLink, useLogout } from 'react-admin';

import grid from '../grid';
import { AppState } from '../types';
import SubMenu from './SubMenu';
import {
    resolveLabel,
    SIDEBAR_SECTIONS,
    SidebarGroup,
    SidebarLink,
} from './sidebarConfig';

interface Props {
    dense: boolean;
    logout: () => void;
    onMenuClick: () => void;
}

const Menu: FC<Props> = ({ onMenuClick, dense, logout }) => {
    const translate = useTranslate();
    const isXSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
    const open = useSelector((state: AppState) => state.admin.ui.sidebarOpen);
    useSelector((state: AppState) => state.theme); // force rerender on theme change
    const label = (text: string) => <span className="menu-label">{text}</span>;
    const performLogout = useLogout();

    const sections = useMemo(() => SIDEBAR_SECTIONS, []);

    return (
        <div className="menu-container menu-shell growth-sidebar">
            <div className="menu-header">
                <div className="menu-brand">
                    <div className="menu-brand-mark">
                        <FlashOnIcon fontSize="small" />
                    </div>
                    <div className="menu-brand-text">
                        <div className="menu-title">Sprive Admin</div>
                        <div className="menu-subtitle">Control Center</div>
                    </div>
                </div>
                <Tooltip title="Logout">
                    <IconButton
                        size="small"
                        onClick={performLogout}
                        aria-label="logout"
                        className="menu-logout-btn"
                    >
                        <ExitToAppIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>

            {sections.map((section) => (
                <div key={section.id} className="menu-section-block">
                    <div className="menu-section">{section.title}</div>
                    {section.items.map((item) => {
                        if (item.type === 'group') {
                            return (
                                <SubMenu
                                    key={item.id}
                                    dense={dense}
                                    onMenuClick={onMenuClick}
                                    groups={[item as SidebarGroup]}
                                />
                            );
                        }

                        const link = item as SidebarLink;
                        return (
                            <MenuItemLink
                                key={link.to}
                                to={link.to}
                                primaryText={label(resolveLabel(link, translate))}
                                leftIcon={<grid.icon />}
                                sidebarIsOpen={open}
                                dense={dense}
                                onClick={onMenuClick}
                                className="sidebar-leaf-link"
                            />
                        );
                    })}
                </div>
            ))}

            {isXSmall && (
                <MenuItemLink
                    to="/configuration"
                    primaryText={label(translate('pos.configuration'))}
                    leftIcon={<SettingsIcon />}
                    onClick={onMenuClick}
                    sidebarIsOpen={open}
                    dense={dense}
                />
            )}
            {isXSmall && logout}
        </div>
    );
};

export default Menu;
